import React from 'react';
import { Link } from 'react-router-dom';
import { PlayCircle, Clock, BarChart3, Award, ListChecks, ChevronLeft, ChevronRight, Play, Lock, Crown } from 'lucide-react';
import { useLang, pickLocalized, tx } from '../../i18n/LanguageContext';
import { stripHtml } from '../../lib/richText';
import { fmtClock, fmtDuration, lessonSecs } from '../../lib/curriculum';

const countLabel = (lang, n, [one, two, few, many], en) => {
  if (lang !== 'ar') return `${n} ${en}`;
  if (n === 1) return one;
  if (n === 2) return two;
  return `${n} ${n >= 3 && n <= 10 ? few : many}`;
};

const Stat = ({ Icon, label, value, testId }) => (
  <div data-testid={testId} className="flex items-center gap-3">
    <span className="w-11 h-11 shrink-0 rounded-full bg-[color-mix(in_srgb,var(--ac)_12%,white)] text-[var(--ac)] flex items-center justify-center"><Icon className="w-5 h-5" /></span>
    <div>
      <div className="text-xs sm:text-sm text-slate-500">{label}</div>
      <div className="text-sm sm:text-base font-bold text-slate-900">{value}</div>
    </div>
  </div>
);

export const CourseHero = ({ course, lessons, totalSeconds, canStart, playing, player, playerRef, onStart, onIntro, onPreview, onLocked }) => {
  const { lang, isRTL } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const Chevron = isRTL ? ChevronLeft : ChevronRight;
  const brand = pickLocalized(course.brand, lang);
  const level = pickLocalized(course.level, lang) || L('متوسط', 'Intermediate');
  const intro = lessons[0];
  return (
    <section data-testid="course-hero" style={{ '--ac': course.accent || '#2563eb' }} className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center mb-8 sm:mb-10">
      <div className="art-reveal">
        <Link to="/courses" data-testid="course-back-pill" className="inline-flex items-center gap-3 h-10 px-4 rounded-xl bg-white border border-slate-200 shadow-sm text-sm font-bold text-slate-800 hover:bg-slate-50 mb-7">
          {L('الكورسات', 'Courses')}<Chevron className="w-4 h-4" />
        </Link>
        <div>
          <span data-testid="course-level-badge" className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[color-mix(in_srgb,var(--ac)_12%,white)] text-[var(--ac)] text-sm font-bold mb-5">
            {L('مستوى', 'Level')} {level}<BarChart3 className="w-4 h-4" />
          </span>
        </div>
        <h1 data-testid="course-title" className="text-3xl sm:text-4xl lg:text-[2.75rem] font-black text-slate-900 leading-[1.15] tracking-tight mb-4">
          <span dir="auto">{brand && <span className="text-[var(--ac)]">{brand}</span>}{brand && ' '}{pickLocalized(course.title, lang)}</span>
          {stripHtml(pickLocalized(course.subtitle, lang)) && <span className="block text-2xl sm:text-3xl lg:text-4xl mt-2">{stripHtml(pickLocalized(course.subtitle, lang))}</span>}
        </h1>
        <p data-testid="course-hero-desc" className="text-slate-500 text-base sm:text-lg leading-relaxed line-clamp-3 mb-8 max-w-2xl">{stripHtml(pickLocalized(course.description, lang))}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mb-9">
          <Stat testId="course-stat-lessons" Icon={PlayCircle} label={L('عدد الدروس', 'Lessons')} value={countLabel(lang, lessons.length, ['درس واحد', 'درسان', 'دروس', 'درساً'], 'lessons')} />
          <Stat testId="course-stat-hours" Icon={Clock} label={L('المدة', 'Duration')} value={fmtDuration(totalSeconds, lang) || '—'} />
          <Stat testId="course-stat-level" Icon={BarChart3} label={L('مستوى الدورة', 'Course level')} value={level} />
          <Stat testId="course-stat-cert" Icon={Award} label={L('شهادة إتمام', 'Certificate')} value={L('متوفرة', 'Available')} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {canStart ? (
            <button data-testid="start-course-btn" onClick={onStart} disabled={!lessons.length}
              className="h-11 sm:h-12 rounded-xl bg-[var(--ac)] hover:brightness-110 disabled:opacity-50 text-white text-base font-extrabold inline-flex items-center justify-center gap-3 shadow-xl shadow-[color-mix(in_srgb,var(--ac)_30%,transparent)] transition-[filter]">
              <span className="w-8 h-8 rounded-full bg-white text-[var(--ac)] flex items-center justify-center"><Play className="w-4 h-4 fill-current ms-0.5" /></span>
              {lessons.length ? L('ابدأ التعلم الآن', 'Start learning now') : L('لا توجد دروس بعد', 'No lessons yet')}
            </button>
          ) : (
            <button data-testid="subscribe-btn" onClick={onLocked}
              className="btn-subscribe h-11 sm:h-12 text-base gap-3">
              <Crown className="w-5 h-5" />{L('اشترك للوصول', 'Subscribe to access')}
            </button>
          )}
          <button data-testid="preview-content-btn" onClick={onPreview}
            className="h-11 sm:h-12 rounded-xl bg-white border border-slate-200 hover:border-blue-200 hover:bg-blue-50/40 text-slate-900 text-base font-bold inline-flex items-center justify-center gap-3 shadow-sm transition-colors">
            <ListChecks className="w-6 h-6 text-slate-400" />{L('معاينة محتوى الدورة', 'Preview course content')}
          </button>
        </div>
      </div>

      <div ref={playerRef} data-testid="course-player" className="art-reveal relative rounded-[28px] overflow-hidden aspect-[16/15] sm:aspect-[4/3] bg-slate-900 shadow-2xl shadow-slate-300/60 scroll-mt-28" style={{ animationDelay: '120ms' }}>
        {playing ? <div className="absolute inset-0 flex items-center bg-black">{player}</div> : (
          <>
            <img src={course.image} alt={pickLocalized(course.title, lang)} className="w-full h-full object-cover" />
            {intro && (
              <button data-testid="watch-intro-btn" onClick={onIntro}
                className="group absolute bottom-6 start-6 flex items-center gap-4 ps-3 pe-6 py-3 rounded-full bg-slate-900/75 backdrop-blur-xl border border-white/15 text-white shadow-2xl hover:bg-slate-900/90 transition-colors">
                <span className="w-14 h-14 rounded-full bg-[var(--ac)] flex items-center justify-center group-hover:scale-110 transition-transform">
                  {canStart ? <Play className="w-6 h-6 fill-white ms-0.5" /> : <Lock className="w-6 h-6" />}
                </span>
                <span className="text-start">
                  <span className="block font-extrabold text-base sm:text-lg">{L('شاهد مقدمة الدورة', 'Watch the course intro')}</span>
                  {fmtClock(lessonSecs(intro)) && <span className="block text-sm text-slate-300" dir="ltr">{fmtClock(lessonSecs(intro))}</span>}
                </span>
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
};
